import React, { useEffect, useRef, useState } from "react";
import { DoubleArrow } from "@mui/icons-material";
import * as Mui from "@mui/material";
import { Box, Typography, Paper, IconButton, Drawer } from "@mui/material";
import { CustomTextBox } from "components/CustomComponents/CustomTextBox";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { parentBoxStyle, themes } from "services/constants";
import { showToast } from "services/utils/Status";
import { CustomButton } from "components/CustomComponents/CustomButton";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  ifscCodePatterns,
  atleastOneCharacterWithSpace,
} from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";

export const EditBankDetails = ({
  setOpenSidePeek,
  openSidePeek,
  editBankDetails,
  reloadData,
  setReloadData,
  bulkData,
  setBulkData,
  importedData,
  setImportData,
  setButtonDisabled,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    reset,
  } = useForm({
    defaultValues: {
      employee_id: "",
      user_name: "",
      bankname: "",
      accountno: "",
      ifsc: "",
      branch_name: "",
      rowid: "",
    },
  });

  const employeeIdVal  = useWatch({ control, name: "employee_id" });
  const userNameVal    = useWatch({ control, name: "user_name" });
  const bankNameVal    = useWatch({ control, name: "bankname" });
  const accountNoVal   = useWatch({ control, name: "accountno" });
  const ifscVal        = useWatch({ control, name: "ifsc" });
  const branchNameVal  = useWatch({ control, name: "branch_name" });

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [errorMessage, setErrorMessage] = useState(editBankDetails?.error_messages || "");

  const initializedForId = useRef(null);

  useEffect(() => {
    if (!openSidePeek) {
      initializedForId.current = null;
      return;
    }

    if (openSidePeek && editBankDetails && initializedForId.current !== editBankDetails.rowid) {
      const fullRow = importedData?.find((r) => r.rowid === editBankDetails.rowid) || {};

      reset({
        employee_id: fullRow.employee_id  || editBankDetails.employee_id  || "",
        user_name:   fullRow.user_name    || editBankDetails.user_name    || "",
        bankname:    fullRow.bankname     || editBankDetails.bankname     || "",
        accountno:   fullRow.accountno    || editBankDetails.accountno    || "",
        ifsc:        fullRow.ifsc         || editBankDetails.ifsc         || "",
        branch_name: fullRow.branch_name  || editBankDetails.branch_name  || "",
        rowid:       fullRow.rowid        || editBankDetails.rowid        || "",
      });

      setErrorMessage(editBankDetails.error_messages || "");
      initializedForId.current = editBankDetails.rowid;
      setReloadData(false);
    }
  }, [openSidePeek, editBankDetails, importedData, reset, setReloadData]);

  const handleUpdateBankDetails = async (data) => {
    const payload = [
      {
        rowid: editBankDetails?.rowid,
        employee_id: data.employee_id,
        user_name: data.user_name,
        bankname: data.bankname,
        accountno: data.accountno,
        ifsc: data.ifsc,
        branch_name: data.branch_name,
      },
    ];

    try {
      const response = await fetch(`${domain}bulk-bank-import/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const res = await response.json();

      if (response.ok) {
        setErrorMessage(res[0].error_messages || "");

        const updatedBankDetails = importedData.map((modifyData) => {
          if (modifyData.rowid === editBankDetails.rowid) {
            return {
              ...modifyData,
              employee_id:   res[0].employee_id,
              user_name:     res[0].user_name,
              bankname:      res[0].bankname,
              accountno:     res[0].accountno,
              ifsc:          res[0].ifsc,
              branch_name:   data.branch_name,
              status:        res[0].is_valid ? "Valid" : "Invalid",
              is_valid:      res[0].is_valid,
              error_messages: res[0].error_messages,
            };
          }
          return modifyData;
        });

        setImportData(updatedBankDetails);
        setBulkData(updatedBankDetails);
        setButtonDisabled(updatedBankDetails.some((r) => r.is_valid === false));

        if (!res[0].error_messages) {
          showToast("success", "Bank details verified.");
          setOpenSidePeek(false);
        } else {
          showToast("error", res[0].error_messages);
        }
      } else {
        throw new Error('REQUEST_FAILED');
      }
    } catch (error) {
      console.error('[BulkEditBankDetails] operation failed:', error);
      showToast("error", "Couldn't save the bank details. Please try again.");
    }
  };

  const handleAccountNoChange = (e) => {
    const val = e.target.value;
    if (onlyNumberRegex.test(val) && val.length <= 17) {
      setValue("accountno", val, { shouldValidate: true });
    }
  };

  const handleIfscChange = (e) => {
    const val = e.target.value;
    if (specialCharacterRestrict.test(val) && val.length <= 11) {
      setValue("ifsc", val, { shouldValidate: true });
    }
  };

  const handleBankNameChange = (e) => {
    const val = e.target.value;
    if (specialCharacterRestrict.test(val) && val.length <= 64) {
      setValue("bankname", val, { shouldValidate: true });
    }
  };

  const handleUserNameChange = (e) => {
    const val = e.target.value;
    if (specialCharacterRestrict.test(val) && val.length <= 64) {
      setValue("user_name", val, { shouldValidate: true });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={openSidePeek}
      onClose={() => setOpenSidePeek(false)}
      PaperProps={{
        sx: { width: { xs: 300, sm: 360, md: 500, lg: 600 } },
        height: "calc(100% - 64px)",
        top: 64,
      }}
    >
      <Paper elevation={0} sx={{ borderBottom: `1px solid ${themes?.miliedLightGray}`, borderRadius: 0 }}>
        <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ padding: 1, backgroundColor: themes?.primary }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={() => { reset(); setOpenSidePeek(false); }} sx={{ color: themes?.grayColor }}>
              <DoubleArrow />
            </IconButton>
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Edit Bank Details</Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <Box sx={parentBoxStyle}>
          <form onSubmit={handleSubmit(handleUpdateBankDetails)}>
            {errorMessage && (
              <Mui.Grid container sx={{ px: 1 }}>
                <Mui.Grid item xs={12}>
                  <Mui.Box
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      px: 2, py: 1.25, m: 3, mb: 0, borderRadius: 1.5,
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <ErrorOutlineRoundedIcon sx={{ color: themes.backgroundRed, fontSize: 20 }} />
                    <Mui.Typography variant="body2" sx={{ color: themes.backgroundRed, fontWeight: 500 }}>
                      {errorMessage}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.Grid>
              </Mui.Grid>
            )}

            <Mui.Paper sx={{ p: 2, m: 2 }}>
              <Mui.Grid container spacing={2}>

                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>Employee ID</Typography>
                  <CustomTextBox
                    selectedValue={employeeIdVal || ""}
                    rules={{ ...register("employee_id", { required: "Employee ID is required" }) }}
                    updateDataFunction={(e) => setValue("employee_id", e.target.value, { shouldValidate: true })}
                    errorMessage={errors?.employee_id?.message}
                    textBoxError={!!errors.employee_id}
                  />
                </Mui.Grid>

                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>Account Holder Name</Typography>
                  <CustomTextBox
                    selectedValue={userNameVal || ""}
                    rules={{
                      ...register("user_name", {
                        required: "Account Holder Name is required",
                        validate: {
                          minLengthNoSpaces: (value) => {
                            if (value?.trim().length < 3 && value !== "") return minimum3Characters;
                            return true;
                          },
                          atleastOneChar: (value) => {
                            if (atleastOneCharacterWithSpace.test(value) || value === "") return true;
                            return "At least one alphabet required";
                          },
                        },
                      }),
                    }}
                    updateDataFunction={handleUserNameChange}
                    errorMessage={errors?.user_name?.message}
                    textBoxError={!!errors.user_name}
                  />
                </Mui.Grid>

                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>Bank Name</Typography>
                  <CustomTextBox
                    selectedValue={bankNameVal || ""}
                    rules={{
                      ...register("bankname", {
                        required: "Bank Name is required",
                        validate: {
                          minLengthNoSpaces: (value) => {
                            if (value?.trim().length < 3 && value !== "") return minimum3Characters;
                            return true;
                          },
                          atleastOneChar: (value) => {
                            if (atleastOneCharacterWithSpace.test(value) || value === "") return true;
                            return "At least one alphabet required";
                          },
                        },
                      }),
                    }}
                    updateDataFunction={handleBankNameChange}
                    errorMessage={errors?.bankname?.message}
                    textBoxError={!!errors.bankname}
                  />
                </Mui.Grid>

                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>Account Number</Typography>
                  <CustomTextBox
                    selectedValue={accountNoVal || ""}
                    rules={{
                      ...register("accountno", {
                        required: "Account Number is required",
                        pattern: {
                          value: onlyNumberRegex,
                          message: "Only numbers allowed",
                        },
                        maxLength: {
                          value: 17,
                          message: "Account number must be at most 17 digits",
                        },
                        minLength: {
                          value: 9,
                          message: "Account number must be at least 9 digits",
                        },
                      }),
                    }}
                    updateDataFunction={handleAccountNoChange}
                    errorMessage={errors?.accountno?.message}
                    textBoxError={!!errors.accountno}
                  />
                </Mui.Grid>

                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>IFSC Code</Typography>
                  <CustomTextBox
                    selectedValue={ifscVal || ""}
                    rules={{
                      ...register("ifsc", {
                        required: "IFSC Code is required",
                        validate: {
                          ifscFormat: (value) => {
                            const trimmed = value?.trim();
                            if (!trimmed) return true;
                            if (trimmed.length < 11) return "Minimum 11 characters required";
                            if (!ifscCodePatterns.test(trimmed)) return "Enter a valid format: ABCD0123456";
                            return true;
                          },
                        },
                      }),
                    }}
                    updateDataFunction={handleIfscChange}
                    errorMessage={errors?.ifsc?.message}
                    textBoxError={!!errors.ifsc}
                  />
                </Mui.Grid>

                {/* Branch Name is now strictly marked required */}
                <Mui.Grid item xs={6}>
                  <Typography sx={{ fontWeight: 600 }}>Branch Name</Typography>
                  <CustomTextBox
                    selectedValue={branchNameVal || ""}
                    rules={{
                      ...register("branch_name", {
                        required: "Branch Name is required",
                      }),
                    }}
                    updateDataFunction={(e) =>
                      setValue("branch_name", e.target.value, { shouldValidate: true })
                    }
                    errorMessage={errors?.branch_name?.message}
                    textBoxError={!!errors.branch_name}
                  />
                </Mui.Grid>

              </Mui.Grid>
            </Mui.Paper>

            <Mui.Grid container sx={{ padding: 2 }}>
              <Mui.Grid xs={6}>
                <CustomButton actionFuntion={() => setOpenSidePeek(false)} actionName="Cancel" typeName="button" />
              </Mui.Grid>
              <Mui.Grid xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <CustomButton actionName="Re-Verify" typeName="submit" />
              </Mui.Grid>
            </Mui.Grid>
          </form>
        </Box>
      </Box>
    </Drawer>
  );
};